import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from "axios";
import { useAuth } from "@/shared/auth/store";
import { env } from "@/shared/config/env";

/**
 * Single axios instance, used by every feature.
 *
 * * Auth header is attached from the auth store on every request.
 * * On 401 we attempt exactly ONE refresh (with deduplication so a burst
 *   of parallel calls all wait on the same in-flight refresh promise).
 * * Errors are normalised into the RFC 9457 problem-details shape we use
 *   on the server, and surfaced via `ApiProblem`.
 */

export interface ApiProblem {
  status: number;
  code?: string | undefined;
  title?: string | undefined;
  detail?: string | undefined;
  request_id?: string | undefined;
  errors?: Record<string, string[] | string> | string[] | undefined;
  /** 429 로그인 잠금(`code=login_locked`)일 때 풀릴 때까지 남은 초. */
  retry_after?: number | undefined;
}

export class ApiError extends Error implements ApiProblem {
  status: number;
  code?: string | undefined;
  title?: string | undefined;
  detail?: string | undefined;
  request_id?: string | undefined;
  errors?: ApiProblem["errors"];
  retry_after?: number | undefined;

  constructor(p: ApiProblem) {
    super(p.detail ?? p.title ?? `HTTP ${p.status}`);
    this.status = p.status;
    this.code = p.code;
    this.title = p.title;
    this.detail = p.detail;
    this.request_id = p.request_id;
    this.errors = p.errors;
    this.retry_after = p.retry_after;
  }
}

export const api: AxiosInstance = axios.create({
  baseURL: `${env.apiBase}/api/v1`,
  timeout: 30_000,
  withCredentials: false,
});

// ── Request: bearer token ──────────────────────────────────────────────────
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const access = useAuth.getState().access;
  if (access) config.headers.set("Authorization", `Bearer ${access}`);
  return config;
});

// ── Response: refresh-once on 401 ─────────────────────────────────────────
let refreshing: Promise<string | null> | null = null;

async function refreshAccess(): Promise<string | null> {
  const { refresh, clear } = useAuth.getState();
  if (!refresh) return null;
  try {
    const res = await axios.post(
      `${env.apiBase}/api/v1/auth/refresh`,
      { refresh },
      { timeout: 15_000 },
    );
    const next = res.data?.access as string | undefined;
    const newRefresh = (res.data?.refresh as string | undefined) ?? refresh;
    if (!next) throw new Error("no_access_in_refresh");
    useAuth.setState({ access: next, refresh: newRefresh });
    return next;
  } catch {
    clear();
    return null;
  }
}

api.interceptors.response.use(
  (r) => r,
  async (err: AxiosError<ApiProblem>) => {
    const original = err.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;

    if (err.response?.status === 401 && original && !original._retry) {
      original._retry = true;
      refreshing ??= refreshAccess().finally(() => {
        refreshing = null;
      });
      const next = await refreshing;
      if (next) {
        original.headers ??= {} as never;
        (original.headers as Record<string, string>).Authorization = `Bearer ${next}`;
        return api(original);
      }
    }

    const data = err.response?.data;

    // Forced password change: the server locks every endpoint except
    // change-password/me/logout/refresh behind a 403 `password_change_required`.
    // Flip the cached user's flag so <PasswordGate> redirects to the dedicated
    // screen (mirrors the `must_change_password` we also read from /auth/me).
    if (err.response?.status === 403 && data?.code === "password_change_required") {
      const u = useAuth.getState().user;
      if (u && !u.must_change_password) {
        useAuth.setState({ user: { ...u, must_change_password: true } });
      }
    }

    throw new ApiError({
      status: err.response?.status ?? 0,
      code: data?.code,
      title: data?.title,
      detail: data?.detail ?? err.message,
      request_id: data?.request_id,
      errors: data?.errors,
      retry_after: typeof data?.retry_after === "number" ? data.retry_after : undefined,
    });
  },
);
